import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  FaCalendarAlt,
  FaCheckCircle,
  FaArchive,
  FaSyncAlt,
  FaArrowLeft,
  FaExclamationTriangle,
} from "react-icons/fa";
import { API_URL } from "../../config";
import "./MonthManagement.css";

const berlinDate = (date, options) =>
  new Intl.DateTimeFormat("de-DE", {
    timeZone: "Europe/Berlin",
    ...options,
  }).format(date);

const monthLabel = (month) => {
  const [year, number] = month.split("-").map(Number);
  return berlinDate(new Date(Date.UTC(year, number - 1, 1)), {
    month: "long",
    year: "numeric",
  });
};

const dayLabel = (day) =>
  berlinDate(new Date(day + "T12:00:00Z"), {
    weekday: "long",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });

const entryDate = (value) => {
  const text = String(value);
  return new Date(
    /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(text)
      ? text.replace(" ", "T") + "Z"
      : text
  );
};

const currentBerlinMonth = () => {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/Berlin",
    year: "numeric",
    month: "2-digit",
  }).formatToParts(new Date());

  return (
    parts.find((part) => part.type === "year").value +
    "-" +
    parts.find((part) => part.type === "month").value
  );
};

const MonthManagement = ({ onDataChanged }) => {
  const [months, setMonths] = useState([]);
  const [closures, setClosures] = useState([]);
  const [days, setDays] = useState([]);
  const [entries, setEntries] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState("");
  const [selectedDay, setSelectedDay] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [revision, setRevision] = useState(0);
  const busyRef = useRef(false);

  const token = localStorage.getItem("token");

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError("");
    setSelectedIds([]);

    const get = async (path) => {
      const response = await fetch(`${API_URL}/api/entries${path}`, {
        headers: { Authorization: `Bearer ${token}` },
        signal: controller.signal,
      });
      const data = await response.json().catch(() => null);

      if (!response.ok || !data) {
        throw new Error(data?.error || "Daten konnten nicht geladen werden.");
      }
      return data;
    };

    const load = async () => {
      try {
        if (selectedDay) {
          setEntries([]);
          const data = await get(
            "/admin/day-entries?day=" + encodeURIComponent(selectedDay)
          );
          if (!Array.isArray(data.entries)) {
            throw new Error("Ungültige Eintragsliste.");
          }
          if (!controller.signal.aborted) setEntries(data.entries);
        } else if (selectedMonth) {
          setDays([]);
          const data = await get(
            "/admin/days?month=" + encodeURIComponent(selectedMonth)
          );
          if (!Array.isArray(data.days)) {
            throw new Error("Ungültige Tagesübersicht.");
          }
          if (!controller.signal.aborted) setDays(data.days);
        } else {
          const data = await get("/admin/months");
          if (!Array.isArray(data.months) || !Array.isArray(data.closures)) {
            throw new Error("Ungültige Monatsübersicht.");
          }
          if (!controller.signal.aborted) {
            setMonths(data.months);
            setClosures(data.closures);
          }
        }
      } catch (err) {
        if (!controller.signal.aborted) setError(err.message);
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };

    load();
    return () => controller.abort();
  }, [token, selectedMonth, selectedDay, revision]);

  const bookableIds = useMemo(
    () =>
      entries
        .filter((entry) => {
          const date = entryDate(entry.time);
          return (
            entry.status === "erfasst" &&
            Number.isSafeInteger(Number(entry.id)) &&
            Number(entry.id) > 0 &&
            !Number.isNaN(date.getTime()) &&
            date.getTime() <= Date.now()
          );
        })
        .map((entry) => Number(entry.id)),
    [entries]
  );

  const selection = selectedIds.filter((id) => bookableIds.includes(id));
  const allSelected =
    bookableIds.length > 0 && selection.length === bookableIds.length;

  const toggle = (id) => {
    if (busyRef.current) return;
    setSelectedIds((previous) =>
      previous.includes(id)
        ? previous.filter((value) => value !== id)
        : [...previous, id]
    );
  };

  const navigate = (month, day = "") => {
    if (busyRef.current) return;
    setSelectedMonth(month);
    setSelectedDay(day);
    setSelectedIds([]);
    setLoading(true);
    setError("");
    setSuccess("");
  };

  const performAction = async (action, body, confirmation, message) => {
    if (busyRef.current || !window.confirm(confirmation)) return;

    busyRef.current = true;
    setBusy(true);
    setError("");
    setSuccess("");

    try {
      const response = await fetch(`${API_URL}/api/entries/${action}`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      });
      const data = await response.json().catch(() => null);

      if (!response.ok || data?.success !== true) {
        throw new Error(data?.error || "Die Aktion wurde nicht bestätigt.");
      }

      setSuccess(message);
      setSelectedIds([]);
      setLoading(true);
      setRevision((value) => value + 1);

      try {
        await onDataChanged?.();
      } catch {
        setSuccess(message + " Bitte die Dashboardübersicht aktualisieren.");
      }
    } catch (err) {
      setError(
        err instanceof TypeError
          ? "Keine Serverantwort erhalten. Bitte aktualisieren und den Status prüfen, bevor du erneut buchst."
          : err.message
      );
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };

  const bookEntries = (ids) => {
    if (!ids.length || loading) return;
    performAction(
      "book-selected",
      { ids },
      `${ids.length} Einträge verbindlich buchen? Danach sind sie gegen Bearbeitung gesperrt.`,
      `${ids.length} Einträge wurden gebucht.`
    );
  };

  const stats = (item) => (
    <div className="month-stats">
      {[
        ["Erfasst", item.erfasst],
        ["Bearbeitet", item.bearbeitet],
        ["Gebucht", item.gebucht],
        ["Archiviert", item.archiviert],
      ].map(([label, value]) => (
        <div key={label}>
          <span>{label}</span>
          <strong>{Number(value || 0)}</strong>
        </div>
      ))}
    </div>
  );

  return (
    <div className="month-management">
      <div className="month-heading">
        <div>
          <span className="month-eyebrow">ABRECHNUNG</span>
          <h2>
            {selectedDay
              ? dayLabel(selectedDay)
              : selectedMonth
                ? monthLabel(selectedMonth)
                : "Monatsverwaltung"}
          </h2>
          <p>
            {selectedDay
              ? "Einträge prüfen und einzeln oder gemeinsam buchen."
              : selectedMonth
                ? "Wähle einen Tag, um seine Einträge zu sehen."
                : "Wähle einen Monat, um seine Tage zu sehen."}
          </p>
        </div>

        <div className="month-navigation">
          {selectedMonth && (
            <button
              type="button"
              className="month-refresh"
              disabled={busy}
              onClick={() =>
                selectedDay ? navigate(selectedMonth) : navigate("")
              }
            >
              <FaArrowLeft />
              {selectedDay ? "Zur Tagesübersicht" : "Zur Monatsübersicht"}
            </button>
          )}
          <button
            type="button"
            className="month-refresh"
            disabled={loading || busy}
            onClick={() => {
              setLoading(true);
              setRevision((value) => value + 1);
            }}
          >
            <FaSyncAlt />
            Aktualisieren
          </button>
        </div>
      </div>

      {error && (
        <div className="month-message month-error" role="alert">
          <FaExclamationTriangle /> {error}
        </div>
      )}
      {success && (
        <div className="month-message month-success" role="status">
          <FaCheckCircle /> {success}
        </div>
      )}

      {loading ? (
        <div className="month-empty">Daten werden geladen …</div>
      ) : error ? null : selectedDay ? (
        <>
          <div className="month-entry-toolbar">
            <label>
              <input
                type="checkbox"
                checked={allSelected}
                disabled={busy || !bookableIds.length}
                ref={(element) => {
                  if (element) {
                    element.indeterminate =
                      selection.length > 0 && !allSelected;
                  }
                }}
                onChange={() =>
                  setSelectedIds(allSelected ? [] : [...bookableIds])
                }
              />
              Alle buchbaren Einträge auswählen
            </label>

            <button
              type="button"
              className="month-book-button month-bulk-button"
              disabled={busy || !selection.length}
              onClick={() => bookEntries(selection)}
            >
              <FaCheckCircle />
              {busy ? "Wird gespeichert …" : `${selection.length} Ausgewählte buchen`}
            </button>
          </div>

          {entries.length === 0 ? (
            <div className="month-empty">Keine Einträge für diesen Tag.</div>
          ) : (
            <div className="month-day-entries">
              {entries.map((entry) => {
                const id = Number(entry.id);
                const canBook = bookableIds.includes(id);
                const name =
                  [entry.user_first_name, entry.user_last_name]
                    .filter(Boolean)
                    .join(" ") || entry.user_email;
                const date = entryDate(entry.time);
                const statusLabel = {
                  erfasst: "Erfasst",
                  gebucht: "Gebucht",
                  archiviert: "Archiviert",
                }[entry.status] || entry.status;

                return (
                  <article className="month-card" key={entry.id}>
                    <div className="month-card-header">
                      <div>
                        <strong>{name}</strong>
                        <p className="month-entry-email">{entry.user_email}</p>
                      </div>
                      <input
                        type="checkbox"
                        checked={selection.includes(id)}
                        disabled={busy || !canBook}
                        onChange={() => toggle(id)}
                        aria-label={`Eintrag ${id} auswählen`}
                      />
                    </div>

                    <span className={`month-badge month-${entry.status}`}>
                      {statusLabel}
                    </span>
                    <h3>{entry.code}</h3>
                    <p className="month-entry-activity">{entry.activity}</p>
                    <p className="month-subtitle">
                      {Number.isNaN(date.getTime())
                        ? "Ungültige Zeit"
                        : berlinDate(date, {
                            hour: "2-digit",
                            minute: "2-digit",
                          }) + " Uhr"}
                    </p>

                    {canBook ? (
                      <div className="month-actions">
                        <button
                          type="button"
                          className="month-book-button"
                          disabled={busy}
                          onClick={() => bookEntries([id])}
                        >
                          <FaCheckCircle /> Diesen Eintrag buchen
                        </button>
                      </div>
                    ) : (
                      <div className="month-locked">
                        {entry.status === "erfasst"
                          ? "Derzeit nicht buchbar"
                          : "Eintrag abgeschlossen"}
                      </div>
                    )}
                  </article>
                );
              })}
            </div>
          )}
        </>
      ) : selectedMonth ? (
        days.length === 0 ? (
          <div className="month-empty">Keine Einträge in diesem Monat.</div>
        ) : (
          <div className="month-grid">
            {days.map((day) => (
              <article className="month-card" key={day.day}>
                <button
                  type="button"
                  className="month-open"
                  disabled={busy}
                  onClick={() => navigate(selectedMonth, day.day)}
                >
                  <FaCalendarAlt />
                  <h3>{dayLabel(day.day)}</h3>
                  <span>{day.total} Einträge · Tag öffnen</span>
                </button>
                {stats(day)}
              </article>
            ))}
          </div>
        )
      ) : months.length === 0 ? (
        <div className="month-empty">Noch keine Arbeitsmonate vorhanden.</div>
      ) : (
        <div className="month-grid">
          {months.map((month) => {
            const closure = closures.find(
              (item) => item.month_key === month.month
            );
            const status = closure?.status || "offen";
            const canBook =
              status === "offen" && month.month < currentBerlinMonth();

            return (
              <article className="month-card" key={month.month}>
                <div className="month-card-header">
                  <div className="month-icon"><FaCalendarAlt /></div>
                  <span className={`month-badge month-${status}`}>
                    {status === "offen"
                      ? "Offen"
                      : status === "gebucht"
                        ? "Gebucht"
                        : "Archiviert"}
                  </span>
                </div>

                <button
                  type="button"
                  className="month-open"
                  disabled={busy}
                  onClick={() => navigate(month.month)}
                >
                  <h3>{monthLabel(month.month)}</h3>
                  <span>{month.total} Einträge · Tage öffnen</span>
                </button>

                {stats(month)}

                <div className="month-actions">
                  {canBook && (
                    <button
                      type="button"
                      className="month-book-button"
                      disabled={busy}
                      onClick={() =>
                        performAction(
                          "book-month",
                          { month: month.month },
                          `${monthLabel(month.month)} vollständig buchen und abschließen?`,
                          `${monthLabel(month.month)} wurde gebucht.`
                        )
                      }
                    >
                      <FaCheckCircle /> Monat buchen
                    </button>
                  )}
                  {status === "gebucht" && (
                    <button
                      type="button"
                      className="month-archive-button"
                      disabled={busy}
                      onClick={() =>
                        performAction(
                          "archive-month",
                          { month: month.month },
                          `${monthLabel(month.month)} archivieren?`,
                          `${monthLabel(month.month)} wurde archiviert.`
                        )
                      }
                    >
                      <FaArchive /> Archivieren
                    </button>
                  )}
                  {status === "archiviert" && (
                    <div className="month-locked">Monat abgeschlossen</div>
                  )}
                  {status === "offen" && !canBook && (
                    <div className="month-current">
                      Einzelne Einträge können über die Tagesansicht gebucht werden.
                    </div>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MonthManagement;